import { ThreadState, ThreadTask } from '@langchain/langgraph-sdk';

import { SchemaBundle } from '@/Pages/Graph/src/control/state';

import { ForeignData, parseForeignData } from './foreign-data';
import * as Tree from './tree';

// ---- Graph ----

export type Node = {
  id: string;
  data: string | Record<string, unknown>;
  metadata?: Record<string, unknown>;
};

export type Edge = {
  source: string;
  target: string;
  conditional: boolean;
  data?: unknown;
};

export type Graph = {
  nodes: Node[];
  edges: Edge[];
  subgraphs: Record<string, SchemaBundle>;
};

// ---- Debug Output ----

export type TaskPayload<TaskMeta> = {
  id: string;
  name: string;
  input: TaskMeta;
  triggers: string[];
  checkpoint_id: string;
  parent_checkpoint_id: string | null;
};

export type Task = {
  type: 'task';
  namespace: string;
  timestamp: string;
  step: number;
  payload: TaskPayload<unknown>;
};

export type TaskResultPayload<TaskResultMeta> = {
  id: string;
  name: string;
  // as of  v0.4.7 in langgraph js, results are streamed as objects, but before it was an array
  result: Array<[string, TaskResultMeta]> | Record<string, TaskResultMeta>;
  error?: string;
  interrupts?: ThreadTask['interrupts'];
  checkpoint_id: string;
  parent_checkpoint_id: string | null;
};

export type TaskResult = {
  type: 'task_result';
  namespace: string;
  timestamp: string;
  step: number;
  payload: TaskResultPayload<unknown>;
};

export type CheckpointPayload<CheckpointMeta> = ThreadState<
  Record<string, CheckpointMeta>
>;

export type Checkpoint = {
  type: 'checkpoint';
  namespace: string;
  timestamp: string;
  step: number;
  payload: CheckpointPayload<unknown>;
};

export type Updates = Task | TaskResult | Checkpoint;

// ---- Derived Types ----

export type StepGroup = {
  step: number;
  updates: Array<Updates>;
};

export type TaskState =
  | { type: 'running'; task: Task }
  | { type: 'completed'; task: Task; result: TaskResult };

export type Step = {
  tasks: Array<TaskState>;
};

export type CheckpointEntry = {
  type: 'checkpoint';
  id: string;
  name: string;
  state: ThreadState;
};

export type InputEntry = {
  type: 'input';
  id: string;
  name: string;
  data: ForeignData;
  readonly?: boolean;
};

export type NodeEntry = {
  type: 'node';
  id: string;
  name: string;
  data: ForeignData;
  readonly?: boolean;
  error?: unknown;
};

export type PendingEntry = {
  type: 'pending';
  id: string;
  name: string;
  data: ForeignData;
};

export type ErrorEntry = {
  type: 'error';
  id: string;
  name: string;
  error: unknown;
};

export type TraceLog = {
  meta: {
    step: number;
    timestamp: string;
    next: string[];
    id: string;
    runId: string | null;
    parentId: string | null | undefined;
    forks: Array<Tree.ForestPath>;
    path: Tree.ForestPath;
    source: ('input' | 'loop' | 'task' | (string & {})) | null;
  };
  // TODO: move away from TraceLog and reuse ThreadState
  state: ThreadState | undefined;
  task?: ThreadState['tasks'][number];
  entry: CheckpointEntry | InputEntry | NodeEntry | PendingEntry | ErrorEntry;
};

// ---- Helpers ----

export const traceLogKey = (traceLog: TraceLog) => `${traceLog.entry.id}`;

export const causedBy = ({
  source,
  target,
  triggers,
  previousTaskNames,
}: {
  source: string;
  target: string;
  triggers: Array<
    | { type: 'start'; node: string }
    | { type: 'node'; node: string }
    | { type: 'branch'; source: string; target: string; edgeName: string }
  >;
  previousTaskNames: Array<string>;
}) =>
  triggers.some((trigger) => {
    if (trigger.type === 'node') {
      return (
        trigger.node === source &&
        previousTaskNames.length &&
        previousTaskNames.some((t) => t === source)
      );
    }
    if (trigger.type === 'start' && source === '__start__') {
      return trigger.node === target && previousTaskNames.length === 0;
    }

    if (trigger.type === 'branch') {
      return trigger.source === source && trigger.target === target;
    }
    return false;
  });

export const getLoadingTasks = (updates: Array<Updates>): Task[] => {
  const tasks = updates.filter(isTask);
  const hasResult = new Set(
    updates.filter(isTaskResult).map((taskResult) => taskResult.payload.id)
  );

  return tasks.filter((task) => !hasResult.has(task.payload.id));
};

type HistoryMetadataAssumption = {
  step: number;
  writes:
    | null
    | Record<string, Record<string, unknown>>
    | Record<string, unknown>;
  source: string | null;
  run_id: string | null;
};

export function getCheckpointId<T extends ThreadState | undefined>(
  x: T
): Exclude<T, ThreadState> | string {
  // @ts-expect-error The guard already comes from the generic
  if (!x) return undefined;

  return (
    x.checkpoint?.checkpoint_id ??
    // @ts-expect-error checkpoint_id is deprecated, but app assumes the location for now
    x.checkpoint_id ??
    x.checkpoint?.checkpoint_id
  );
}

export function getParentCheckpointId(x: ThreadState): string | null {
  return (
    // @ts-expect-error checkpoint_id is deprecated
    x.parent_checkpoint_id ?? x.parent_checkpoint?.checkpoint_id ?? null
  );
}

export const historyToTraceLog = (
  input: Tree.FlatTree<ThreadState>
): Array<TraceLog> => {
  const { value, path, forks } = input;

  const stateMeta = value.metadata as unknown as HistoryMetadataAssumption;
  const meta: TraceLog['meta'] = {
    next: value.next,
    timestamp: value.created_at ?? '',
    id: getCheckpointId(value),
    parentId: getParentCheckpointId(value),
    step: stateMeta.step,
    runId: stateMeta.run_id ?? null,
    source: stateMeta.source ?? null,
    forks,
    path,
  };

  const taskLogs =
    value.tasks?.flatMap((task): TraceLog => {
      let entry: TraceLog['entry'];
      if (task.error && !task.result) {
        entry = {
          type: 'error',
          id: task.id,
          name: task.name,
          error: task.error,
        };
      } else if (task.result) {
        entry = {
          // TODO: set as input to get input schema instead of state schema?
          type: 'node',
          id: task.id,
          name: task.name,
          data: parseForeignData(task.result),
          error: task.error,
        };
      } else {
        entry = {
          type: 'pending',
          id: task.id,
          name: task.name,
          data: parseForeignData({}),
        };
      }

      return {
        entry,
        state: value,
        task,
        meta: { ...meta, id: task.id ?? meta.id },
      };
    }) ?? [];

  return [
    {
      entry: { type: 'checkpoint', id: meta.id, name: meta.id, state: value },
      state: value,
      meta,
    },
    ...taskLogs,
  ];
};

export function isTask(update: Updates): update is Task {
  return update.type === 'task';
}

export function isTaskResult(update: Updates): update is TaskResult {
  return update.type === 'task_result';
}

export function isCheckpoint(update: Updates): update is Checkpoint {
  return update.type === 'checkpoint';
}
